import { Box, Stack, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";

/**
 * One sector in the picker, with its follow-up question when it has one.
 *
 * Split from the picker because the expanded state — a question and a wrap of
 * pills — roughly doubles the markup, and because the row is the part with the
 * interesting accessibility: two levels of toggle, each of which has to say
 * what it is and whether it is pressed.
 *
 * Both levels are real `<button>`s with `aria-pressed`, matching `GoalCard`.
 * Selected state is carried by a tick and a border as well as colour, which
 * ECH-116's review checklist asks for.
 *
 * @param {object} props
 * Takes ids and resolves copy itself, so the value that reaches `goalData` is
 * never the label the member happened to see.
 *
 * @param {string} props.sector          sector id
 * @param {boolean} props.selected
 * @param {string[]} props.chosenOptions  sub-type ids picked for this sector
 * @param {string[]} [props.options]  sub-type ids, when this sector has them
 * @param {"seeking"|"offering"} props.mode  selects the question wording
 * @param {() => void} props.onToggle
 * @param {(option: string) => void} props.onToggleOption
 * @param {boolean} props.divider
 */
const SectorRow = ({
  sector,
  selected,
  chosenOptions = [],
  options,
  mode,
  onToggle,
  onToggleOption,
  divider,
}) => {
  const expanded = Boolean(selected && options?.length);
  const label = t(`onboarding.vocab.${sector}`);

  return (
    <Box
      sx={{
        borderBottom: divider ? `1px solid ${onboardingTokens.track}` : "none",
      }}
      data-sector={sector}
      data-selected={selected ? "true" : "false"}
    >
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-pressed={selected}
        // Two jobs on one control: it selects the sector and, for sectors with
        // sub-types, reveals the follow-up question below, so a screen reader
        // needs both states. Undefined for sectors without sub-types, which
        // would otherwise advertise a disclosure they lack.
        aria-expanded={options?.length ? expanded : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          px: 2,
          py: 1.5,
          textAlign: "left",
          border: "none",
          cursor: "pointer",
          backgroundColor: selected
            ? onboardingTokens.lightTeal
            : "transparent",
          transition: "background-color 0.1s",
          "&:hover": {
            backgroundColor: selected
              ? onboardingTokens.lightTeal
              : onboardingTokens.muted,
          },
          "&:focus-visible": {
            outline: `2px solid ${onboardingTokens.teal}`,
            outlineOffset: -2,
          },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            flexShrink: 0,
            width: 18,
            height: 18,
            borderRadius: "0.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: selected ? onboardingTokens.teal : "transparent",
            border: `1.5px solid ${
              selected
                ? onboardingTokens.teal
                : onboardingTokens.mutedForeground
            }`,
          }}
        >
          {selected && (
            <CheckIcon sx={{ fontSize: 12, color: "primary.contrastText" }} />
          )}
        </Box>

        {/* Long sector names must wrap rather than push the row sideways —
            `Pilot (Helicopter / Rotary-Wing)` overflows at 360px otherwise. */}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "0.875rem",
            fontWeight: selected ? 600 : 400,
            overflowWrap: "anywhere",
          }}
        >
          {label}
        </Typography>

        {expanded && (
          <ExpandMoreIcon
            aria-hidden="true"
            sx={{ fontSize: 16, flexShrink: 0, color: onboardingTokens.teal }}
          />
        )}
      </Box>

      {expanded && (
        <Box
          sx={{ px: 2, pb: 1.5, backgroundColor: onboardingTokens.lightTeal }}
        >
          <Typography
            id={`${sector}-question`}
            sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: 1.25 }}
          >
            {t(`onboarding.sectors.questions.${sector}.${mode}`)}
          </Typography>

          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            role="group"
            aria-labelledby={`${sector}-question`}
          >
            {options.map((option) => {
              const picked = chosenOptions.includes(option);
              return (
                <Box
                  key={option}
                  component="button"
                  type="button"
                  onClick={() => onToggleOption(option)}
                  aria-pressed={picked}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    cursor: "pointer",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    fontFamily: onboardingTokens.fontBody,
                    color: picked
                      ? "primary.contrastText"
                      : onboardingTokens.navy,
                    backgroundColor: picked
                      ? onboardingTokens.teal
                      : onboardingTokens.card,
                    border: `1.5px solid ${
                      picked ? onboardingTokens.teal : onboardingTokens.track
                    }`,
                    "&:focus-visible": {
                      outline: `2px solid ${onboardingTokens.teal}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {t(`onboarding.vocab.${option}`)}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default SectorRow;
